import {
  BedOffIcon,
  BedOnIcon,
  DoorOffIcon,
  DoorOnIcon,
} from 'assets/images/svg';
import { string2money } from 'common/utils/string';
import Stack from 'components/commons/stack';
import Text from 'components/commons/text';
import Button from 'components/elements/button';
import { format } from 'date-fns';
import { enUS, id } from 'date-fns/locale';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { ArrowRight } from 'react-feather';
import { CSS } from 'styles';

import {
  AvailableFacilityContainer,
  AvailableFacilityText,
  ChairLeftText,
  DateText,
  FacilityContentText,
  FromPriceText,
  LocationText,
  PortText,
  PriceText,
  ShipText,
  SmallLabel,
  StyledTicketCard,
  styles,
  TimeText,
} from './style';

export interface TicketCardProps {
  cruiseName: string;
  sourcePort: string;
  sourceLocation: string;
  destinationPort: string;
  destinationLocation: string;
  passenger?: string;
  vehicle?: string;
  departureTime: string;
  departureDayCycle: string;
  arrivalTime: string;
  arrivalDayCycle: string;
  price: string;
  facility?: {
    mattressAvailable: boolean;
    roomAvailable: boolean;
  };
  chairLefts?: number;
  departureDate: Date;
  css?: CSS;
  query?: string;
  onNextState?: () => void;
  arrivalDate: Date;
  key?: string;
  voyageCode: string;
}

export default function TicketCard(props: TicketCardProps) {
  const { t } = useTranslation();
  const {
    arrivalTime,
    cruiseName,
    departureDate,
    departureTime,
    destinationLocation,
    destinationPort,
    facility,
    passenger,
    price,
    chairLefts,
    sourceLocation,
    sourcePort,
    vehicle,
    query,
    css,
    arrivalDate,
    arrivalDayCycle,
    departureDayCycle,
    onNextState,
  } = props;
  const { locale, push } = useRouter();

  return (
    <StyledTicketCard css={css}>
      <Stack gap="md">
        <Stack direction="horizontal" alignItems="center" gap="xxs">
          <Text variant="body2">{t('common:ship')} : </Text>
          <ShipText>{cruiseName}</ShipText>
        </Stack>
        <Stack rootCss={styles.bottomContainer} grow>
          <Stack gap="md">
            <Stack direction="horizontal" grow gap="md">
              <Stack rootCss={{ width: 'auto', '@md': { width: 300 } }}>
                <TimeText>{`${departureTime} ${departureDayCycle}`}</TimeText>
                <DateText css={{ display: 'none', '@md': { display: 'flex' } }}>
                  {format(departureDate, 'EEEE, dd MMMM yyyy', {
                    locale: locale === 'en' ? enUS : id,
                  })}
                </DateText>
                <DateText css={{ display: 'flex', '@md': { display: 'none' } }}>
                  {format(departureDate, 'dd MMMM yyyy', {
                    locale: locale === 'en' ? enUS : id,
                  })}
                </DateText>
                <Stack gap="xxs">
                  <PortText>{sourcePort}</PortText>
                  <LocationText>{sourceLocation}</LocationText>
                </Stack>
              </Stack>
              <ArrowRight />
              <Stack rootCss={{ width: 'auto', '@md': { width: 300 } }}>
                <TimeText>{`${arrivalTime} ${arrivalDayCycle}`}</TimeText>
                <DateText css={{ display: 'none', '@md': { display: 'flex' } }}>
                  {format(arrivalDate, 'EEEE, dd MMMM yyyy', {
                    locale: locale === 'en' ? enUS : id,
                  })}
                </DateText>
                <DateText css={{ display: 'flex', '@md': { display: 'none' } }}>
                  {format(arrivalDate, 'dd MMMM yyyy', {
                    locale: locale === 'en' ? enUS : id,
                  })}
                </DateText>
                <Stack gap="xxs">
                  <PortText>{destinationPort}</PortText>
                  <LocationText>{destinationLocation}</LocationText>
                </Stack>
              </Stack>
            </Stack>
            <Stack rootCss={styles.passengerVehicleContainer}>
              {!!passenger && (
                <Stack.Item css={{ marginRight: 16 }}>
                  <SmallLabel variant="body6">
                    {t('common:passenger_count')}
                  </SmallLabel>
                  <FacilityContentText>{passenger}</FacilityContentText>
                </Stack.Item>
              )}
              {!!vehicle && (
                <Stack.Item css={{ marginRight: 16 }}>
                  <SmallLabel variant="body6">
                    {t('common:vehicle_type')}
                  </SmallLabel>
                  <FacilityContentText>{vehicle}</FacilityContentText>
                </Stack.Item>
              )}
            </Stack>
          </Stack>
          <Stack grow gap="md" rootCss={styles.rightSideContainer} wrap>
            <Stack rootCss={styles.priceContainer} alignItems="center">
              <Stack gap="sm" rootCss={styles.priceContent}>
                <FromPriceText>{t('common:start_from')}</FromPriceText>
                <PriceText>Rp {string2money(price)}</PriceText>
              </Stack>
              {!!chairLefts && chairLefts <= 5 && (
                <ChairLeftText>
                  {t('common:chair_lefts', { count: chairLefts })}
                </ChairLeftText>
              )}
              {!!facility && (
                <Stack rootCss={{ mt: 8 }}>
                  <Stack direction="horizontal" gap="md">
                    <AvailableFacilityContainer>
                      {facility.mattressAvailable ? (
                        <BedOnIcon />
                      ) : (
                        <BedOffIcon />
                      )}
                      <AvailableFacilityText
                        isAvailable={facility.mattressAvailable}
                      >
                        {t(
                          `common:${
                            facility.mattressAvailable
                              ? 'mattress_available'
                              : 'mattress_not_available'
                          }`,
                        )}
                      </AvailableFacilityText>
                    </AvailableFacilityContainer>
                    <AvailableFacilityContainer>
                      {facility.roomAvailable ? (
                        <DoorOnIcon />
                      ) : (
                        <DoorOffIcon />
                      )}
                      <AvailableFacilityText
                        isAvailable={facility.roomAvailable}
                      >
                        {t(
                          `common:${
                            facility.roomAvailable
                              ? 'room_available'
                              : 'room_not_available'
                          }`,
                        )}
                      </AvailableFacilityText>
                    </AvailableFacilityContainer>
                  </Stack>
                </Stack>
              )}
            </Stack>
            <Button
              onClick={() => {
                query && push(query);
                onNextState && onNextState();
              }}
              css={styles.selectButton}
            >
              {t('common:select')}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </StyledTicketCard>
  );
}
